import React, { Fragment, useState } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import {
    ArrowRightOnRectangleIcon,
    Bars3Icon,
    BanknotesIcon,
    CalendarDaysIcon,
    ClipboardDocumentListIcon,
    Cog6ToothIcon,
    GlobeAltIcon,
    InboxStackIcon,
    SparklesIcon,
    UsersIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './components/AuthContext';
import { useApprovalQueue } from './hooks/useApprovalQueue';
import { usePermissions } from './hooks/usePermissions';
import { cx } from './components/ui';

const navigation = [
    { name: 'Dashboard', to: '/home/dashboard', icon: CalendarDaysIcon },
    // Requests are created and read from here, so the form pages keep this lit.
    {
        name: 'My requests',
        to: '/home/request',
        icon: ClipboardDocumentListIcon,
        alsoActiveOn: ['/home/form', '/home/save-the-date'],
    },
    { name: 'Approvals', to: '/home/approvals', icon: InboxStackIcon, badge: 'approvals' },
    { name: 'Administrator', to: '/home/administrator', icon: Cog6ToothIcon },
    // Only shown to people who hold the role (admins see everything). The page
    // gates too, so a hand-typed URL still can't get in.
    { name: 'Finance', to: '/home/finance', icon: BanknotesIcon, requires: 'Finance' },
    { name: 'Human Resources', to: '/home/human-resources', icon: UsersIcon, requires: 'HR' },
    { name: "Pastor's tools", to: '/home/pastors-tools', icon: SparklesIcon },
    // Edits the public church website. The page itself gates to admins.
    { name: 'Website', to: '/home/website', icon: GlobeAltIcon },
];

const NavItems = ({ approvalCount, canView, onNavigate }) => {
    const { pathname } = useLocation();

    return (
        <nav className="flex-1 space-y-0.5 px-3">
            {navigation
                .filter((item) => !item.requires || canView(item.requires))
                .map((item) => {
                const claimsRoute = (item.alsoActiveOn || []).some((path) => pathname.startsWith(path));

                return (
                    <NavLink
                        key={item.name}
                        to={item.to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                            cx(
                                'group relative flex items-center gap-3 rounded-md py-2 pl-3 pr-2 text-sm font-medium transition-colors',
                                isActive || claimsRoute
                                    ? 'bg-brand-50 text-brand-800'
                                    : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                            )
                        }
                    >
                        {({ isActive }) => {
                            const active = isActive || claimsRoute;
                            return (
                                <>
                                    {/* The active marker is the only place oxblood appears in the nav. */}
                                    <span
                                        aria-hidden="true"
                                        className={cx(
                                            'absolute inset-y-1 left-0 w-0.5 rounded-full',
                                            active ? 'bg-brand-700' : 'bg-transparent'
                                        )}
                                    />
                                    <item.icon
                                        className={cx(
                                            'h-5 w-5 flex-shrink-0',
                                            active ? 'text-brand-700' : 'text-neutral-400'
                                        )}
                                        aria-hidden="true"
                                    />
                                    <span className="flex-1 truncate">{item.name}</span>
                                    {item.badge === 'approvals' && approvalCount > 0 && (
                                        <span className="ml-auto rounded-full bg-brand-700 px-1.5 py-0.5 text-xxs font-semibold tabular-nums text-white">
                                            {approvalCount}
                                        </span>
                                    )}
                                </>
                            );
                        }}
                    </NavLink>
                );
            })}
        </nav>
    );
};

const Wordmark = () => (
    <div className="flex items-center gap-2.5 px-5 py-5">
        <img src={process.env.PUBLIC_URL + '/bethel-favicon.png'} alt="" className="h-8 w-auto" />
        <div className="leading-tight">
            <div className="font-display text-base font-semibold tracking-tight text-neutral-900">Bethel</div>
            <div className="text-xxs uppercase tracking-widest text-neutral-400">Event Forms</div>
        </div>
    </div>
);

const UserBlock = ({ user, onSignOut }) => (
    <div className="border-t border-neutral-200 p-3">
        <div className="flex items-center gap-3 px-2 py-1.5">
            {user?.picture ? (
                <img className="h-8 w-8 rounded-full" src={user.picture} alt="" />
            ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-200 text-xs font-semibold text-neutral-600">
                    {(user?.name || '?').charAt(0)}
                </span>
            )}
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-neutral-900">{user?.name}</p>
                <p className="truncate text-xs text-neutral-500">{user?.email}</p>
            </div>
        </div>
        <button
            type="button"
            onClick={onSignOut}
            className="mt-1 flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
        >
            <ArrowRightOnRectangleIcon className="h-5 w-5 text-neutral-400" aria-hidden="true" />
            Sign out
        </button>
    </div>
);

export default function MainView() {
    const { user, realUser, isImpersonating, stopImpersonation, signOut } = useAuth();
    const navigate = useNavigate();
    const { count: approvalCount } = useApprovalQueue();
    const { canAccess } = usePermissions();
    const [mobileOpen, setMobileOpen] = useState(false);

    const exitImpersonation = () => {
        stopImpersonation();
        navigate('/home/administrator');
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const sidebar = (
        <div className="flex h-full flex-col">
            <Wordmark />
            <NavItems approvalCount={approvalCount} canView={canAccess} onNavigate={() => setMobileOpen(false)} />
            <UserBlock user={user} onSignOut={handleSignOut} />
        </div>
    );

    return (
        <div className="min-h-screen bg-neutral-50">
            {/* Desktop sidebar */}
            <aside className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-64 lg:flex-col lg:border-r lg:border-neutral-200 lg:bg-white">
                {sidebar}
            </aside>

            {/* Mobile drawer */}
            <Transition show={mobileOpen} as={Fragment}>
                <Dialog onClose={setMobileOpen} className="relative z-50 lg:hidden">
                    <TransitionChild
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-neutral-900/40" />
                    </TransitionChild>
                    <TransitionChild
                        as={Fragment}
                        enter="transition ease-in-out duration-200 transform"
                        enterFrom="-translate-x-full"
                        enterTo="translate-x-0"
                        leave="transition ease-in-out duration-150 transform"
                        leaveFrom="translate-x-0"
                        leaveTo="-translate-x-full"
                    >
                        <DialogPanel className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl">
                            <button
                                type="button"
                                onClick={() => setMobileOpen(false)}
                                className="absolute right-2 top-4 rounded-md p-2 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
                            >
                                <span className="sr-only">Close menu</span>
                                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                            {sidebar}
                        </DialogPanel>
                    </TransitionChild>
                </Dialog>
            </Transition>

            {/* Mobile top bar */}
            <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-neutral-200 bg-white px-4 py-3 lg:hidden">
                <button
                    type="button"
                    onClick={() => setMobileOpen(true)}
                    className="-ml-1 rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
                >
                    <span className="sr-only">Open menu</span>
                    <Bars3Icon className="h-5 w-5" aria-hidden="true" />
                </button>
                <img src={process.env.PUBLIC_URL + '/bethel-favicon.png'} alt="" className="h-7 w-auto" />
                <span className="font-display text-base font-semibold text-neutral-900">Bethel Event Forms</span>
                {approvalCount > 0 && (
                    <span className="ml-auto rounded-full bg-brand-700 px-2 py-0.5 text-xxs font-semibold tabular-nums text-white">
                        {approvalCount}
                    </span>
                )}
            </div>

            <main className="lg:pl-64">
                {isImpersonating && (
                    <div className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-amber-500 px-4 py-2 text-sm text-amber-950 sm:px-6 lg:px-8">
                        <span>
                            Viewing as <strong>{user?.name || user?.email}</strong> — read-only, no changes will be
                            saved. You're signed in as {realUser?.email}.
                        </span>
                        <button
                            type="button"
                            onClick={exitImpersonation}
                            className="rounded-md bg-amber-950/15 px-3 py-1 font-medium transition-colors hover:bg-amber-950/25"
                        >
                            Exit view-as
                        </button>
                    </div>
                )}
                <Outlet />
            </main>
        </div>
    );
}
